import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import {
  MatButtonToggle,
  MatButtonToggleGroup
} from '@angular/material/button-toggle';
import { MatOption, MatSelect } from '@angular/material/select';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';

import { EMPTY, filter, forkJoin } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

import { ProcessService } from '@app/services/process.service';
import {
  DirectoryTypeEnum,
  DynamicDirectoriesEnum
} from '@app/enums/directories/directory.enum';
import { DirectoriesService } from '@app/services/directories.service';
import { BaseItem, ProcessTemplate } from '@app/shared/models';
import {
  CommercialDocumentListItem,
  CreateCommercialDocumentData
} from '@app/pages/commercial-documents/components/commercial-document-list/models/commercial-document-list.model';
import {
  CommercialDocumentProcessType,
  SourceType
} from '@app/pages/commercial-documents/components/commercial-document-list/enums/commercial-document-list.enum';
import { ChooseCommercialDocumentModalComponent } from '@app/shared/components/modals/choose-commercial-document-modal/choose-commercial-document-modal.component';
import {
  CustomValidators,
  DirectoryMapperUtils,
  validateForm
} from '@app/shared/utils';
import { CommercialDocumentListService } from '@app/pages/commercial-documents/components/commercial-document-list/services/commercial-document-list.service';
import { sourceTypeOptions } from './constants/create-commercial-document-modal.constants';
import { setNeededProcessType } from './helpers/set-needed-process-type.helper';

@Component({
  selector: 'app-create-commercial-document-modal',
  imports: [
    CommonModule,
    FormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatButton,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatSelect,
    MatOption,
    MatError,
    TranslatePipe,
    CdkDrag,
    CdkDragHandle
  ],
  templateUrl: './create-commercial-document-modal.component.html',
  styleUrl: './create-commercial-document-modal.component.scss'
})
export class CreateCommercialDocumentModalComponent implements OnInit {
  public form: FormGroup;
  public commercialDocumentTypes: BaseItem[] = [];
  public processTemplateGroups: any[] = [];
  public processTemplates: ProcessTemplate[] = [];
  public filteredProcessTemplates: ProcessTemplate[] = [];
  public selectedRelatedDocument: CommercialDocumentListItem | null;
  public isCreateDisabled = false;

  public readonly cdProcessType = CommercialDocumentProcessType;
  public readonly sourceOptions = sourceTypeOptions(this.data.type);

  constructor(
    private fb: FormBuilder,
    private processService: ProcessService,
    private cdListService: CommercialDocumentListService,
    private destroyRef: DestroyRef,
    private matDialog: MatDialog,
    private router: Router,
    private dialogRef: MatDialogRef<CreateCommercialDocumentModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      type: CommercialDocumentProcessType;
      moveNext: (id: string) => void;
    },
    private directoriesService: DirectoriesService
  ) {}

  get isRelatedDocumentNeed(): boolean {
    return (
      this.form.get('orderSourceType')?.value === SourceType.Agreement ||
      this.form.get('orderSourceType')?.value === SourceType.PurchaseRequest
    );
  }

  ngOnInit(): void {
    this.getData();

    this.initForm();

    this.orderSourceTypeSubscription();
    this.procedureType();
  }

  public onCreate(): void {
    validateForm(this.form);

    if (
      this.form.valid &&
      (this.selectedRelatedDocument || !this.isRelatedDocumentNeed)
    ) {
      const value = this.form.value;

      const requestBody: CreateCommercialDocumentData = {
        name: value.name,
        processTemplateId: value.processTemplateId!,
        parentCommercialDocumentId: this.selectedRelatedDocument?.id || null,
        commercialDocumentProcessType: this.data.type,
        commercialDocumentTypeId: value.commercialDocumentType?.id,
        commercialDocumentTypeName: value.commercialDocumentType?.name,
        orderSourceType: value.orderSourceType,
        orderSourceId: this.selectedRelatedDocument?.id || null
      };

      this.createCommercialDocument(requestBody);
    }
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public openListModal(): void {
    this.matDialog
      .open(ChooseCommercialDocumentModalComponent, {
        width: '1250px',
        data: {
          type: this.form.get('orderSourceType')?.value
        }
      })
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe((relatedDocument: CommercialDocumentListItem) => {
        this.selectedRelatedDocument = relatedDocument;
      });
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: this.fb.control(this.data.type, [
        Validators.required,
        CustomValidators.noWhiteSpace
      ]),
      orderSourceType: this.fb.control(SourceType.Catalog, Validators.required),
      procedureType: this.fb.control('', Validators.required),
      processTemplateId: this.fb.control('', Validators.required),
      commercialDocumentType: this.fb.control('', Validators.required)
    });
  }

  private orderSourceTypeSubscription(): void {
    this.form
      .get('orderSourceType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.selectedRelatedDocument = null;
      });
  }

  private procedureType(): void {
    this.form
      .get('procedureType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(procedureType => {
        this.filteredProcessTemplates = this.processTemplates.filter(
          item =>
            item.processTemplateGroupDynamicDirectoryId === procedureType.id[0]
        );
      });
  }

  private createCommercialDocument(data: CreateCommercialDocumentData): void {
    this.isCreateDisabled = true;

    this.cdListService
      .createCommercialDocument(data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          if (this.data.moveNext) {
            this.data.moveNext(res.id as string);
          } else {
            this.goToPage(res.id);
          }
          this.dialogRef.close();
        },
        error: () => {
          this.isCreateDisabled = false;
          return EMPTY;
        }
      });
  }

  private goToPage(id: string): void {
    switch (this.data.type) {
      case CommercialDocumentProcessType.Agreement: {
        this.router.navigate([
          `commercial-documents/agreements/agreement-list/${id}`
        ]);
        break;
      }
      case CommercialDocumentProcessType.Order: {
        this.router.navigate([`commercial-documents/orders/order-list/${id}`]);
        break;
      }
      case CommercialDocumentProcessType.Receipt: {
        this.router.navigate([
          `commercial-documents/receipts/receipt-list/${id}`
        ]);
        break;
      }
    }
  }

  private getData(): void {
    forkJoin([
      this.processService.getProcessList(),
      this.directoriesService.getDirectoryEntries(
        DynamicDirectoriesEnum.ProcessTemplateGroups,
        DirectoryTypeEnum.Dynamic
      ),
      this.directoriesService.getDirectoryEntries(
        DynamicDirectoriesEnum.CommercialDocumentTypes,
        DirectoryTypeEnum.Dynamic
      )
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([
          processTemplates,
          processTemplateGroups,
          commercialDocumentTypes
        ]) => {
          const filteredPTIds: string[] = [];

          this.processTemplates = processTemplates.filter(pt => {
            if (pt.processTypeId === setNeededProcessType(this.data.type)) {
              filteredPTIds.push(pt.processTemplateGroupDynamicDirectoryId);
              return true;
            }
            return false;
          });

          this.processTemplateGroups = processTemplateGroups.dataList.filter(
            ptg => filteredPTIds.includes(ptg.id[0])
          );

          const cdByType = commercialDocumentTypes.dataList.filter(item =>
            (item['proccess Type'] as string[])[0]
              .toLowerCase()
              .replaceAll(' ', '')
              .includes(this.data.type.toLowerCase())
          );

          this.commercialDocumentTypes = DirectoryMapperUtils.TransformData(
            cdByType,
            'name'
          );

          if (this.data.type === CommercialDocumentProcessType.Order) {
            this.form
              .get('commercialDocumentType')
              ?.setValue(this.commercialDocumentTypes[0]);
          }
        }
      );
  }
}
